import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, Mail, MapPin } from "lucide-react";
import { tenantHref } from "@/lib/tenantPath";
import ContactForm from "@/components/site/ContactForm";
import { TemplateShell } from "./shared/TemplateShell";
import { BoldHeader, BoldFooter, BOLD_MAIN_CLASS } from "./chrome/BoldChrome";
import { useSiteContent, useTestimonials, useBlogPosts, useServices } from "./shared/useSiteData";
import BlogGrid from "./shared/BlogGrid";
// Relative, not "@/": Hero.fillerText.test.js mounts this template with only a handful of
// "@/" modules virtual-mocked (no craco jest moduleNameMapper for "@/") — see Hero.jsx's
// own identical note.
import { hasText } from "../lib/siteContent";
import { formatQuote } from "../lib/testimonials";

/**
 * Bold / Modern — brutaliste dark + néon. Nav noire uppercase · hero full-bleed
 * sombre typographie massive · grilles à filets néon sans radii · ombres décalées
 * dures · marquee · boutons néon/noir avec offset shadow.
 */
/**
 * The shared ContactForm in this theme's idiom: no radii anywhere, 1px white hairlines that
 * flip to neon on focus, monospace micro labels (the form element carries `style={mono}` so
 * JetBrains Mono is inherited), and the same neon block button with the hard offset shadow
 * as the hero CTA.
 */
const FORM_CLASSES = {
  grid: "grid grid-cols-1 sm:grid-cols-2 gap-5",
  fieldWide: "sm:col-span-2",
  label: "block text-[10px] uppercase tracking-widest text-white/50",
  input: "mt-2 w-full bg-transparent border border-white/20 px-4 py-3 outline-none focus:border-[var(--tpl-accent)] transition-colors",
  textarea: "mt-2 w-full bg-transparent border border-white/20 px-4 py-3 outline-none focus:border-[var(--tpl-accent)] transition-colors resize-none",
  hint: "mt-3 text-[10px] uppercase tracking-widest text-white/50",
  actions: "mt-8 flex items-center gap-6 flex-wrap",
  submit: "inline-flex items-center gap-2 px-8 py-4 font-bold uppercase text-black bg-[var(--tpl-accent)] hover:bg-white hover:shadow-[6px_6px_0_#CCFF00] transition-all disabled:opacity-50",
  success: "text-xs uppercase tracking-widest text-[var(--tpl-accent)]",
  error: "text-xs uppercase tracking-widest text-red-400",
};

export default function BoldTemplate({ template, toggles = {} }) {
  const c = useSiteContent(toggles);
  const testimonials = useTestimonials();
  const posts = useBlogPosts();
  const services = useServices();
  const show = (k) => toggles?.[k] !== false;
  const mono = { fontFamily: "'JetBrains Mono', monospace" };

  const marqueeWords = [...services].map((s) => s.title).concat(c.brandName);

  return (
    <TemplateShell template={template} branding={c.branding}>
      <main data-testid="tpl-bold" className={BOLD_MAIN_CLASS}>
        {/* The header is the theme's CHROME (chrome/BoldChrome.jsx), the same component the
            interior pages render through ThemedPageLayout — never an inline copy. */}
        <BoldHeader toggles={toggles} />

        {/* HERO */}
        <section id="top" className="relative min-h-screen flex items-end overflow-hidden" data-testid="hero-section">
          <div className="absolute inset-0">
            <img src={c.images.heroBg} alt="" className="w-full h-full object-cover opacity-40 grayscale" data-testid="hero-portrait" />
            <div className="absolute inset-0" style={{ background: "linear-gradient(0deg, var(--tpl-bg) 5%, rgba(10,10,10,0.4) 60%, var(--tpl-bg) 100%)" }} />
          </div>
          <div className="relative max-w-7xl mx-auto px-6 md:px-10 pb-24 pt-40 w-full">
            {/* BUG-210: no dictionary sample text backs this any more (lib/content.js) — same
                guard idiom as Hero.jsx's hero-eyebrow. The "//" decorator lives inside the
                guard: it is not real content, but it must not survive alone either. */}
            {hasText(c.hero.eyebrow) && (
              <span style={mono} className="text-sm" data-testid="hero-eyebrow"><span style={{ color: "var(--tpl-accent)" }}>// {c.hero.eyebrow}</span></span>
            )}
            <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="tpl-head text-6xl sm:text-7xl md:text-8xl font-bold uppercase leading-[0.9] mt-4 max-w-5xl">
              {c.hero.titleLines.map((l, i) => <span key={i} className="block">{l}</span>)}
            </motion.h1>
            {c.hero.sub && <p className="mt-7 text-lg md:text-xl max-w-xl text-white/70">{c.hero.sub}</p>}
            <div className="mt-9 flex flex-wrap gap-4">
              <a href={tenantHref("/reserver")} data-testid="hero-primary-cta" className="inline-flex items-center gap-2 px-8 py-4 font-bold uppercase text-black hover:bg-white hover:shadow-[6px_6px_0_#CCFF00] transition-all" style={{ background: "var(--tpl-accent)" }}>{c.hero.cta} <ArrowRight size={18} /></a>
              <a href="#services" className="inline-flex items-center gap-2 px-8 py-4 font-bold uppercase border" style={{ borderColor: "var(--tpl-accent)", color: "var(--tpl-accent)" }}>{c.hero.ctaSecondary}</a>
            </div>
            {/* BUG-196: bare `grid-cols-3` forced three columns at every width, including
                mobile. Single column below `sm`, 3-up restored from `sm:` (640px) up. */}
            <div data-testid="hero-stats" className="mt-14 grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-lg border-t pt-6" style={{ borderColor: "rgba(255,255,255,0.15)" }}>
              {c.hero.stats.map((s, i) => <div key={i}><div className="tpl-head text-3xl font-bold" style={{ color: "var(--tpl-accent)" }}>{s.v}</div><div style={mono} className="text-[10px] uppercase tracking-widest text-white/50 mt-1">{s.l}</div></div>)}
            </div>
          </div>
        </section>

        {/* MARQUEE */}
        <div className="py-5 overflow-hidden whitespace-nowrap" style={{ background: "var(--tpl-accent)" }}>
          <div className="marquee-track inline-flex gap-10 text-black tpl-head text-2xl font-bold uppercase">
            {[...marqueeWords, ...marqueeWords, ...marqueeWords].map((w, i) => <span key={i} className="inline-flex items-center gap-10">{w} <span>✦</span></span>)}
          </div>
        </div>

        {/* SERVICES — grille néon */}
        {show("services") && services.length > 0 && (
          <section id="services" className="py-24 md:py-32 px-6 md:px-10" data-testid="services-section">
            <div className="max-w-7xl mx-auto">
              <span style={{ ...mono, color: "var(--tpl-accent)" }} className="text-sm">// {c.services.eyebrow}</span>
              <h2 className="tpl-head text-4xl md:text-6xl font-bold uppercase mt-3 max-w-3xl whitespace-pre-line">{c.services.title}</h2>
              <p className="mt-5 text-lg text-white/60 max-w-2xl">{c.services.lead}</p>
              <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3">
                {services.map((s, i) => (
                  <motion.a key={i} href={tenantHref("/reserver")} initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }} data-testid={`service-card-${i}`} className="group block p-9 border hover:bg-[var(--tpl-accent)] hover:text-black transition-colors" style={{ borderColor: "rgba(255,255,255,0.18)" }}>
                    <div style={mono} className="flex items-center justify-between text-xs uppercase opacity-60"><span>{s.tag}</span><span>{s.duration}</span></div>
                    <h3 className="tpl-head text-2xl font-bold uppercase mt-5">{s.title}</h3>
                    <p className="mt-3 text-sm leading-relaxed opacity-70 group-hover:opacity-100">{s.body}</p>
                    <div className="mt-6 inline-flex items-center gap-1 text-sm font-bold uppercase">{c.services.cta} <ArrowUpRight size={16} /></div>
                  </motion.a>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* STORY */}
        {show("story") && (
          <section id="story" className="py-24 md:py-32 px-6 md:px-10 border-t" style={{ borderColor: "rgba(255,255,255,0.15)" }} data-testid="story-section">
            <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-12">
              <div>
                <span style={{ ...mono, color: "var(--tpl-accent)" }} className="text-sm">// {c.story.eyebrow}</span>
                <h2 className="tpl-head text-4xl md:text-5xl font-bold uppercase mt-3 whitespace-pre-line">{c.story.title}</h2>
                <p className="mt-5 text-white/60 text-lg">{c.story.lead}</p>
              </div>
              <div className="space-y-8">
                {c.story.chapters.map((ch) => (
                  <motion.div key={ch.n} initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} data-testid={`story-chapter-${ch.n}`} className="border-l-2 pl-5" style={{ borderColor: "var(--tpl-accent)" }}>
                    <span style={{ ...mono, color: "var(--tpl-accent)" }} className="text-xs">{ch.n}</span>
                    <h3 className="tpl-head text-xl font-bold uppercase mt-1">{ch.title}</h3>
                    <p className="mt-2 text-white/60 text-sm">{ch.body}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* APPROACH — the pillars in the same zero-radius hairline grid as the services
            above, numbered in the mono/neon register the whole theme indexes things with
            (hero eyebrow, story chapters, testimonial prompts). */}
        {show("approach") && (
          <section id="approach" className="py-24 md:py-32 px-6 md:px-10 border-t" style={{ borderColor: "rgba(255,255,255,0.15)" }} data-testid="approach-section">
            <div className="max-w-7xl mx-auto">
              <span style={{ ...mono, color: "var(--tpl-accent)" }} className="text-sm">// {c.approach.eyebrow}</span>
              <h2 className="tpl-head text-4xl md:text-6xl font-bold uppercase mt-3 max-w-3xl whitespace-pre-line">{c.approach.title}</h2>
              <p className="mt-5 text-lg text-white/60 max-w-2xl">{c.approach.lead}</p>
              <div className="mt-12 grid md:grid-cols-2">
                {c.approach.pillars.map((p, i) => (
                  <motion.div key={i} initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }} data-testid={`approach-pillar-${i}`} className="p-9 border" style={{ borderColor: "rgba(255,255,255,0.18)" }}>
                    <span style={{ ...mono, color: "var(--tpl-accent)" }} className="text-xs">{String(i + 1).padStart(2, "0")}</span>
                    <h3 className="tpl-head text-2xl font-bold uppercase mt-4">{p.title}</h3>
                    <p className="mt-3 text-sm leading-relaxed text-white/60">{p.body}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* TESTIMONIALS — terminal */}
        {show("testimonials") && testimonials.length > 0 && (
          <section id="testimonials" className="py-24 px-6 md:px-10 border-t" style={{ borderColor: "rgba(255,255,255,0.15)" }} data-testid="testimonials-section">
            <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-6">
              {testimonials.map((tm, i) => (
                <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.06 }} data-testid={`testimonial-${i}`} className="border p-6" style={{ borderColor: "rgba(255,255,255,0.18)", background: "var(--tpl-secondary)" }}>
                  <div style={{ ...mono, color: "var(--tpl-accent)" }} className="text-xs mb-3">$ ~/avis_{String(i + 1).padStart(2, "0")}</div>
                  <p className="text-white/80 leading-relaxed">{formatQuote(tm.quote)}</p>
                  <div style={mono} className="mt-4 text-sm">— {tm.name}</div>
                </motion.div>
              ))}
            </div>
          </section>
        )}

        {/* BLOG */}
        {show("blog") && posts.length > 0 && (
          <section id="blog" className="py-24 md:py-32 px-6 md:px-10 border-t" style={{ borderColor: "rgba(255,255,255,0.15)" }} data-testid="blog-section">
            <BlogGrid
              posts={posts}
              gridClassName="max-w-7xl mx-auto gap-px"
              gridStyle={{ background: "rgba(255,255,255,0.15)" }}
              cardClassName="p-7 group"
              cardStyle={{ background: "var(--tpl-bg)" }}
              renderCard={(p) => (
                <>
                  <img src={p.cover} alt="" className="w-full h-44 object-cover grayscale group-hover:grayscale-0 transition-all mb-5" />
                  <div style={mono} className="text-[11px] uppercase opacity-50">{p.tag} · {p.date}</div>
                  <h3 className="tpl-head text-lg font-bold uppercase mt-2 group-hover:text-[var(--tpl-accent)] transition-colors">{p.title}</h3>
                  <p className="mt-2 text-sm text-white/60">{p.excerpt}</p>
                </>
              )}
            />
          </section>
        )}

        {/* CONTACT */}
        {show("contact") && (
          <section id="contact" className="py-24 md:py-32 px-6 md:px-10 border-t" style={{ borderColor: "rgba(255,255,255,0.15)" }} data-testid="contact-section">
            <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-14">
              <div>
                <h2 className="tpl-head text-4xl md:text-6xl font-bold uppercase whitespace-pre-line">{c.contact.title}</h2>
                <p className="mt-5 text-lg text-white/60 max-w-md">{c.contact.lead}</p>
                <div className="mt-8 flex flex-col gap-4" style={mono}>
                  <a href={`mailto:${c.contact.info.email}`} className="inline-flex items-center gap-2 hover:text-[var(--tpl-accent)]" data-testid="contact-email-link"><Mail size={16} /> {c.contact.info.email}</a>
                  <span className="inline-flex items-center gap-2"><MapPin size={16} /> {c.contact.info.place}</span>
                </div>
                <a href={tenantHref("/reserver")} className="mt-8 inline-flex items-center gap-2 px-8 py-4 font-bold uppercase border" style={{ borderColor: "var(--tpl-accent)", color: "var(--tpl-accent)" }}>{c.nav.book} <ArrowRight size={18} /></a>
              </div>
              <ContactForm style={mono} classNames={FORM_CLASSES} icon={<ArrowRight size={18} />} />
            </div>
          </section>
        )}

        {/* The footer carries this theme's second marquee: it is decoration ABOVE the
            sitemap, not a substitute for it (BUG-026). See chrome/BoldChrome.jsx. */}
        <BoldFooter toggles={toggles} />
      </main>
    </TemplateShell>
  );
}
